import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { fetchProjectOptions } from '@/api/v4/projects.api';
import { mapProjectOptions } from '@/features/action-panel/meetings/hooks/use-project-options.query';
import { isProjectOption } from '@/helpers/other';
import { prepareSafeInput } from '@/helpers/prepare-safe-input';
import { FormLabel } from '@/components/form/form-label';
import { Error } from '@/ui/input/input';
import type { AsyncSelectProps } from '@/ui/select/async/async-select';
import { FormSelect } from '@/features/action-panel/meetings/ui/form-select';

import type { ContentItemFormInput } from './content-item-validation-schema';

export const ContentItemProjectSelect = () => {
  const { t } = useTranslation('settings', { keyPrefix: 'feedTab' });
  const { control, setValue, trigger } = useFormContext<ContentItemFormInput>();

  const projectOptions: AsyncSelectProps['setOptions'] = async (
    searchPhrase: string,
    page?: number,
  ) => {
    const options = await fetchProjectOptions({
      items: 20,
      page,
      search: prepareSafeInput(searchPhrase),
    });

    return mapProjectOptions(options);
  };

  return (
    <FormLabel
      name="linked-project"
      label={t`articleForm.projectRequired`}
      position="top"
    >
      <Controller
        control={control}
        name="project"
        render={({ field, fieldState }) => (
          <>
            <FormSelect
              defaultValue={field.value}
              setOptions={projectOptions}
              onChange={async newValue => {
                if (isProjectOption(newValue)) {
                  setValue(
                    'project',
                    { label: newValue.label, value: newValue.value },
                    { shouldDirty: true, shouldValidate: true },
                  );
                } else {
                  setValue('project', null, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }

                await trigger();
              }}
              placeholder={t`articleForm.projectPlaceholder`}
              withError={Boolean(fieldState.error)}
            />
            {fieldState.error ? (
              <Error>{fieldState.error.message}</Error>
            ) : null}
          </>
        )}
      />
    </FormLabel>
  );
};
